import { Fragment, type FC } from "react";
import {
  Box,
  Collapse,
  Divider,
  Group,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCalendar,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react";
import { ProductIcon } from "@jasper/forms";
import { isRemovableItemType, type Product } from "@jasper/shared";
import {
  formatDayMonth,
  getProductHeadline,
  ICON_STROKE,
} from "./order-page-v2.utils";
import {
  getProductDetailGroups,
  getProductSummary,
  type ProductDetailGroup,
} from "./product-description.utils";

const DETAIL_GRID_STYLE = {
  gridTemplateColumns: "minmax(120px, 170px) 1fr",
  columnGap: "var(--mantine-spacing-s4)",
  rowGap: "var(--mantine-spacing-s1)",
};

const ProductDetailGroups: FC<{ groups: ProductDetailGroup[] }> = ({
  groups,
}) =>
  groups.map((group, index) => (
    <Fragment key={group.title ?? index}>
      {index > 0 && <Divider color="ink.1" />}
      <Stack gap="s2" py="s3">
        {group.title && <Text variant="overline">{group.title}</Text>}
        <Box display="grid" style={DETAIL_GRID_STYLE}>
          {group.details.map(({ label, value }, detailIndex) => (
            <Fragment key={`${label}-${detailIndex}`}>
              <Text size="sm" c="ink.5">
                {label}
              </Text>
              <Text size="sm" fw={500}>
                {value}
              </Text>
            </Fragment>
          ))}
        </Box>
      </Stack>
    </Fragment>
  ));

type OrderProductLineProps = {
  product: Product;
};

export const OrderProductLine: FC<OrderProductLineProps> = ({ product }) => {
  const [isDetailOpened, { toggle: toggleDetail }] = useDisclosure(false);
  const summary = getProductSummary(product);
  const ChevronIcon = isDetailOpened ? IconChevronUp : IconChevronDown;

  return (
    <Group wrap="nowrap" align="flex-start" gap="s3" py="s3">
      <ProductIcon
        productTypes={[product.productType]}
        isRemovable={isRemovableItemType(product.productType)}
      />
      <Box flex={1} miw={0}>
        <Text variant="strong">{getProductHeadline(product)}</Text>
        {summary && <Text variant="caption">{summary}</Text>}
        <UnstyledButton
          mt="s1"
          aria-expanded={isDetailOpened}
          onClick={toggleDetail}
        >
          <Group gap="s1" wrap="nowrap">
            <Text size="sm" fw={600} c="brand.7">
              {isDetailOpened ? "Masquer le détail" : "Voir le détail"}
            </Text>
            <ChevronIcon
              size={14}
              stroke={ICON_STROKE}
              color="var(--mantine-color-brand-7)"
            />
          </Group>
        </UnstyledButton>
        <Collapse expanded={isDetailOpened}>
          <Paper
            withBorder
            bg="appBackground"
            radius="md"
            px="s3"
            pt="s1"
            pb="s3"
            mt="s2"
          >
            <ProductDetailGroups groups={getProductDetailGroups(product)} />
            {product.createdAt && (
              <>
                <Divider color="ink.1" />
                <Group gap="s1" pt="s3" wrap="nowrap">
                  <IconCalendar
                    size={13}
                    stroke={ICON_STROKE}
                    color="var(--mantine-color-ink-5)"
                  />
                  <Text variant="caption">
                    {`Ajouté le ${formatDayMonth(new Date(product.createdAt))}`}
                  </Text>
                </Group>
              </>
            )}
          </Paper>
        </Collapse>
      </Box>
    </Group>
  );
};
